import { alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled, type Theme, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useQuery } from '@tanstack/react-query'
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartDataset,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverGraphLoader from 'components/KNDetailsPopover/KNDetailsPopoverGraphLoader'
import KNDetailsPopoverSection from 'components/KNDetailsPopover/KNDetailsPopoverSection'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { KNTemperatureTrackingIcon } from 'components/KNIcon/KNMaterialIcon'
import { UserContext } from 'context/authentication/UserContext'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { getDarkModeColors, getTemperatureChartOptions } from 'global/helpers/charts'
import { formatDecimals } from 'global/helpers/numberFormatters'
import { convertCelsiusToFahrenheit, getFormattedUnit } from 'global/helpers/units'
import i18n from 'i18n'
import { groupBy, merge } from 'lodash'
import React, { type ReactElement, useCallback, useContext, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { TrackingHealth, TrackingStatus, type TripData } from 'screens/TripDashboard/TripDashboard.types'
import { MeasurementsSystem } from 'screens/UserManager/UserManager.types'
import { getPickupAndDeliveryTimes, getTemperatureAnnotations, temperatureDataTransformer } from './TripDetails.helpers'
import { tripLegsQueryOptions, tripVehicleTemperaturesQueryOptions } from './TripDetails.service'

interface TemperatureProps {
  trip: TripData
  shipmentNumber?: string
  withGraph?: boolean
  weblinkToken?: string
}

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Legend, Tooltip, annotationPlugin)

export const TemperatureGrid = styled(Grid)(({ theme }: { theme: Theme }) => ({
  '&': {
    borderRadius: '4px',
    backgroundColor: alpha(theme.palette.primary.dark, 0.5),
    padding: '8px',
  },
}))

const Temperature = ({
  trip,
  shipmentNumber,
  withGraph = false,
  weblinkToken,
}: TemperatureProps): ReactElement | null => {
  const { t } = useTranslation()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { userPreferences } = useContext(UserContext)

  const isTracked = Boolean(
    trip.trackingInformation
    && [TrackingStatus.IN_PROGRESS, TrackingStatus.STOPPED].includes(trip.trackingInformation.trackingStatus)
    && trip.trackingInformation.temperatureTrackingStatus !== TrackingHealth.NONE,
  )
  const unit = userPreferences.measurementsSystem === MeasurementsSystem.IMPERIAL ? 'F' : 'C'

  const temperatureDetailsPopover = usePopoverState()
  const legsQuery = useQuery({
    ...tripLegsQueryOptions(trip.entityId, weblinkToken),
    enabled: temperatureDetailsPopover.open,
  })
  const temperaturesQuery = useQuery({
    ...tripVehicleTemperaturesQueryOptions(trip.entityId, weblinkToken),
    enabled: temperatureDetailsPopover.open && isTracked && withGraph,
  })
  const temperatures = useMemo(() => {
    if (!temperaturesQuery.isSuccess) {
      return []
    }
    return temperaturesQuery.data.map((temperature) => ({
      ...temperature,
      temperature: unit === 'F'
        ? convertCelsiusToFahrenheit(temperature.temperature)
        : temperature.temperature,
    }))
  }, [temperaturesQuery, unit])
  const temperatureDatasets = useMemo((): ChartDataset<'line'>[] => {
    if (!legsQuery.isSuccess) {
      return []
    }
    const pickupAndDeliveryTimes = getPickupAndDeliveryTimes(legsQuery.data)
    return temperatureDataTransformer(
      temperatures,
      pickupAndDeliveryTimes.earliestPickup,
      pickupAndDeliveryTimes.latestDelivery,
    )
  }, [legsQuery, temperatures])
  const temperatureRanges = useMemo(() => {
    if (!legsQuery.isSuccess) {
      return []
    }
    const groupedRanges = groupBy(
      legsQuery.data
        .filter((leg) => shipmentNumber ? leg.shipmentNumber === shipmentNumber : true)
        .flatMap((leg) => {
          const range = leg.pharmaExtendedInformation
          if (!range || [range.minimum, range.maximum, range.setPoint].every((value) => value === undefined)) {
            return []
          }
          return [{
            range: {
              ...range,
              minimum: unit === 'F' && range.minimum !== undefined
                ? convertCelsiusToFahrenheit(range.minimum)
                : range.minimum,
              maximum: unit === 'F' && range.maximum !== undefined
                ? convertCelsiusToFahrenheit(range.maximum)
                : range.maximum,
              setPoint: unit === 'F' && range.setPoint !== undefined
                ? convertCelsiusToFahrenheit(range.setPoint)
                : range.setPoint,
            },
            shipmentNumber: leg.shipmentNumber,
          }]
        }),
        ({ range }) => `${range.minimum}-${range.maximum}`,
      )
    return Object.values(groupedRanges).map((group) => ({
      ...group[0]!.range,
      shipmentNumbers: group.map(({ shipmentNumber }) => shipmentNumber),
    }))
  }, [legsQuery, unit])

  const handleTemperatureDetailsClickWrapper = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      temperatureDetailsPopover.handleClick(event)
      analyticsEvent('polestar_cs_trip_temperature_details_popover', [getRouteName(location.pathname)])
    },
    [location],
  )

  if ((!isTracked || (isTracked && !withGraph)) && !trip.temperatureControlled) {
    return null
  }

  return (
    <>
      <IconButton
        onClick={handleTemperatureDetailsClickWrapper}
        size="small"
        sx={{
          padding: 0,
          color: isTracked ? 'primary.main' : 'primary.light',
        }}
      >
        <KNTemperatureTrackingIcon />
      </IconButton>

      <KNDetailsPopover
        open={temperatureDetailsPopover.open}
        onClose={temperatureDetailsPopover.handleClose}
        anchorEl={temperatureDetailsPopover.anchorEl}
        rightAligned
      >
        {legsQuery.isPending && (
          <KNDetailsPopoverGraphLoader />
        )}
        {legsQuery.isSuccess && isTracked && withGraph && (
          <>
            <KNDetailsPopoverSection
              header={trip.tripNumber}
              items={[]}
            />
            {temperaturesQuery.isPending && (
              <KNDetailsPopoverGraphLoader />
            )}
            {temperaturesQuery.isSuccess && (
              <KNDetailsPopoverSection
                items={[
                  <Box
                    key="chart"
                    sx={{
                      width: isMobile ? '20rem' : '30rem',
                      height: isMobile ? '10rem' : '20rem',
                      marginTop: '-1px',
                      paddingX: 1,
                      paddingY: 0.5,
                    }}
                  >
                    <Line
                      options={
                        merge(
                          getTemperatureChartOptions(unit),
                          getDarkModeColors(),
                          getTemperatureAnnotations(temperatureRanges, legsQuery.data),
                        )
                      }
                      data={{ datasets: temperatureDatasets }}
                    />
                  </Box>,
                ]}
              />
            )}
          </>
        )}
        {legsQuery.isSuccess && temperatureRanges.length > 0 && (
          <KNDetailsPopoverSection
            header={t('screens.cs.trip_details.temperature_details.temperature_requirements')}
            items={temperatureRanges.map((temperatureRange) => (
              <Box
                key={temperatureRange.setPoint}
                sx={{
                  width: isMobile ? '20rem' : '30rem',
                  marginTop: '-1px',
                  paddingX: 1,
                  paddingY: 0.5,
                }}
              >
                <TemperatureGrid container>
                  <Grid size={3}>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="p6" color="primary.light" noWrap>
                        {t('screens.cs.trip_details.temperature_details.set_point')}
                      </Typography>
                      <Typography variant="p5" noWrap>
                        {t('screens.cs.trip_details.temperature_details.temperature_in_degrees', {
                          temperature: formatDecimals(temperatureRange.setPoint),
                          setPointUnit: getFormattedUnit(unit),
                        })}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={5}>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="p6" color="primary.light" noWrap>
                        {i18n.t('screens.cs.trip_details.temperature_details.required_temperature_range')}
                      </Typography>
                      <Typography variant="p5" noWrap>
                        {i18n.t('screens.cs.trip_details.temperature_details.temperature_between_ranges', {
                          minTemperature: formatDecimals(temperatureRange.minimum),
                          maxTemperature: formatDecimals(temperatureRange.maximum),
                          minUnit: getFormattedUnit(unit),
                          maxUnit: getFormattedUnit(unit),
                        })}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="p5" component="p">
                      {temperatureRange.shipmentNumbers?.join(', ')}
                    </Typography>
                  </Grid>
                </TemperatureGrid>
              </Box>
            ))}
          />
        )}
      </KNDetailsPopover>
    </>
  )
}

export default Temperature
